import { Text, List } from "@chakra-ui/react"
import Figure, { PopOverProps } from "@hydrologiq/shared/figures/generic/Figure"
import { pctChange, roundToDP } from "@hydrologiq/common/helpers/math"
import { EnergyPeriod } from "@hydrologiq/common/types/energy/consumption"
import { CO2eBasis } from "@hydrologiq/common/types/co2e"
import { EmissionsReport, Emissions as EmissionsType } from "@hydrologiq/gql/graphql"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import TrendIndicator from "@hydrologiq/shared/figures/generic/TrendIndicator"

export interface EmissionsFactorProps {
  period: EnergyPeriod | string
  emissions?: EmissionsType
  error?: string
  loading?: boolean
  title?: string
  isPlaceholder?: boolean
  compact?: boolean
}

function EmissionsFactor({ period, emissions, error, loading = false, title, isPlaceholder, compact = true }: EmissionsFactorProps) {
  const comparisonBasis =
    emissions?.diesel?.factor && emissions?.hydrogen?.factor
      ? undefined
      : emissions?.diesel?.factor
        ? CO2eBasis.HYDROGEN
        : CO2eBasis.DIESEL

  const source: EmissionsReport = {
    factor: (emissions?.diesel?.factor || 0) + (emissions?.hydrogen?.factor || 0),
    total: (emissions?.diesel?.total || 0) + (emissions?.hydrogen?.total || 0),
  }
  if (emissions?.diesel?.factor && emissions?.hydrogen?.factor) source.factor = source.factor / 2

  const comparison: EmissionsReport = {
    factor: (emissions?.diesel?.comparison?.factor || 0) + (emissions?.hydrogen?.comparison?.factor || 0),
    total: (emissions?.diesel?.comparison?.total || 0) + (emissions?.hydrogen?.comparison?.total || 0),
  }

  const unit = "kg/kWh"
  let savings =
    source && comparison
      ? roundToDP(
        comparison.factor < source.factor
          ? pctChange(comparison.factor, source.factor)
          : pctChange(source.factor, comparison.factor),
        0
      )
      : 0
  // let savingsTotal = comparison.factor && comparison.factor !== 0 ? source.factor - comparison.factor : 0
  // if (savingsTotal < 0) savingsTotal *= -1
  const valid = emissions && source && emissions && savings !== undefined

  const popover: undefined | PopOverProps = {
    title: "Information",
    content: (
      <>
        <Text>This number includes CO2 emissions from:</Text>
        <List.Root width={"100%"}>
          <List.Item>Fuel production</List.Item>
          <List.Item>Fuel transportation</List.Item>
          <List.Item>Tailpipe / end-use</List.Item>
        </List.Root>
      </>
    ),
  }
  const comparisonText =
    comparison && comparisonBasis !== undefined
      ? `${roundToDP(comparison.factor, 2)} ${unit} if ${comparisonBasis} is used (${Math.abs(savings)}% ${savings <= 0 ? "saving" : "increase"})`
      : undefined

  return (
    <Figure
      heading={title ?? "CO2 Emissions factor"}
      isPlaceholder={!valid || isPlaceholder}
      error={error}
      loading={loading}
      popover={popover}
      compact={compact}
    >
      {valid && (
        <FigureContent.Root compact={compact}>
          <FigureContent.SubText link={<TrendIndicator change={savings} />} compact={compact}>{period === "today" ? "latest" : period}</FigureContent.SubText>
          <FigureContent.Value compact={compact} tooltip={comparisonText}>
            {roundToDP(source.factor, 2)} {unit}
          </FigureContent.Value>
        </FigureContent.Root>
      )}
    </Figure>
  )
}

export default EmissionsFactor
